import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Fourthline, type EmbeddedWebSdk, type Locale } from '@repo/kyc';
import { useSegment } from '@repo/poly-hooks/segment';
import { useIntl } from 'react-intl';
import { useSentry } from '@repo/shared-kit/sentry';
import { KycProcessStatus, KycProvider } from '@repo/api-kit/queries/due-diligence/kyc';
import { useKycInfo } from '../hooks';
import { useKycContext } from '../context';
import { getFourthlineProviderDetails, isKycExpired } from '../utils';
import { KycError } from '../error';

export function FourthlineProvider() {
  const segment = useSegment();
  const { locale } = useIntl();
  const sentry = useSentry();

  const { legalCountry, trackingPrefix, membershipId, onBack, onComplete, isRegisterFlow } =
    useKycContext();
  const market = isRegisterFlow ? legalCountry : undefined;
  const { data: kycInfo, refetch, isLoading } = useKycInfo(market);
  const cancelled = useRef(false);

  const providerDetails = getFourthlineProviderDetails(kycInfo);
  const processStatus = kycInfo?.activeProcess?.processStatus;

  const token = providerDetails?.token ?? '';
  const redirectUrl = providerDetails?.redirectUrl;

  useEffect(() => {
    return () => {
      cancelled.current = true;
    };
  }, []);

  useEffect(() => {
    if (processStatus === KycProcessStatus.submitted && !cancelled.current) {
      onComplete(KycProvider.fourthline);
    }
  }, [processStatus, onComplete]);

  const handleOnboardingStarted = useCallback(() => {
    segment.track(`${trackingPrefix}_fourthline_verification_screen_loaded`, {
      domain: window.location.hostname,
    });
  }, [segment, trackingPrefix]);

  const handleOnboardingCompleted = useCallback(() => {
    cancelled.current = true;

    onComplete(KycProvider.fourthline);
  }, [onComplete]);

  const handleOnboardingCompletedError = useCallback(() => {
    // The error screen is handled by the WebSDK
    segment.track(`${trackingPrefix}_fourthline_error_screen_loaded`, {
      domain: window.location.hostname,
    });
  }, [segment, trackingPrefix]);

  const handleOnboardingRestartRequest = useCallback(
    async (webSdkElement: { setToken: (token: string) => void }) => {
      segment.track(`${trackingPrefix}_fourthline_regenerate_link_clicked`, {
        domain: window.location.hostname,
      });

      const { data } = await refetch();
      const refreshedToken = data?.activeProcess?.providerDetails?.value.token;

      if (refreshedToken) {
        webSdkElement.setToken(refreshedToken);
      }
    },
    [segment, trackingPrefix, refetch]
  );

  const embeddedWebSdkProps = useMemo(
    () =>
      ({
        locale: locale as Locale,
        token,
        events: {
          desktop: {
            onOnboardingStarted: handleOnboardingStarted,
            onOnboardingCompleted: handleOnboardingCompleted,
            onOnboardingCompletedError: handleOnboardingCompletedError,
            onOnboardingRestartRequest: handleOnboardingRestartRequest,
          },
        },
      }) satisfies EmbeddedWebSdk,
    [
      locale,
      token,
      handleOnboardingStarted,
      handleOnboardingCompleted,
      handleOnboardingCompletedError,
      handleOnboardingRestartRequest,
    ]
  );

  if (isLoading) return null;

  if (processStatus === KycProcessStatus.submitted) return null;

  if (!providerDetails) {
    sentry.captureException(new Error('Fourthline provider details are null'), {
      extra: {
        kycInfo,
        membershipId,
      },
      tags: {
        component: 'FourthlineProvider',
        provider: 'fourthline',
      },
    });

    return <KycError onRetry={refetch} />;
  }

  if (!token || !redirectUrl) {
    sentry.captureException(new Error('Fourthline KYC credentials missing'), {
      extra: {
        kycInfo,
        membershipId,
      },
      tags: {
        component: 'FourthlineProvider',
        provider: 'fourthline',
      },
    });

    return <KycError onRetry={refetch} />;
  }

  const isExpired = isKycExpired(kycInfo);
  const isQesRequired = Boolean(kycInfo?.activeProcess?.signatureRequired);

  return (
    <Fourthline
      embeddedWebSdkProps={embeddedWebSdkProps}
      fourthlineRedirectUrl={redirectUrl}
      isKycExpired={isExpired}
      isKycRequired
      isQesRequired={isQesRequired}
      legalCountry={legalCountry}
      mode={trackingPrefix}
      onBack={onBack}
    />
  );
}
